/**
 * VaultifyWaitlistCore — pure, provider-independent logic shared by the
 * product interaction state and any future picker-specific waitlist
 * renderer (dropdown, swatch, pill, ...).
 *
 * No DOM access. No provider names. No window/document references.
 * Dual export: CommonJS (for `node:test`) and a browser global
 * (window.VaultifyWaitlistCore), loaded as a plain <script> before
 * vaultify-pickers.js — this repo has no bundler, so this is the
 * lightest-weight way to make the logic testable without jsdom while
 * still being consumable by the browser-only picker framework.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.VaultifyWaitlistCore = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ── Variant resolution ──────────────────────────────────────────────────

  /**
   * Reads the option value for a given 0-based axis index from a variant
   * object shaped like Shopify's `product.variants | json` (option1/2/3).
   */
  function optionValueAt(variant, axisIndex) {
    return variant['option' + (axisIndex + 1)];
  }

  /**
   * Resolves a single candidate (one axis, one value) against the current
   * selections on every other axis and the trusted variant array.
   *
   * @param {number} axisIndex - 0-based option position of the candidate.
   * @param {string} value - the candidate option value.
   * @param {Array<string|null|undefined>} otherAxisSelections - current
   *   selection for every axis, indexed by position. The entry at
   *   `axisIndex` is ignored (the candidate value is used instead).
   *   Blank/null/undefined entries are treated as wildcards.
   * @param {Array<object>} variants - trusted variant array.
   * @returns {{state: 'unresolved'|'resolved-available'|'resolved-sold-out'|'invalid', variant: object|null}}
   */
  function resolveVariantCandidate(axisIndex, value, otherAxisSelections, variants) {
    if (value === undefined || value === null || value === '') {
      return { state: 'unresolved', variant: null };
    }
    if (!Array.isArray(variants) || !variants.length) {
      return { state: 'invalid', variant: null };
    }

    const matches = variants.filter((variant) => {
      if (String(optionValueAt(variant, axisIndex)) !== String(value)) return false;
      return otherAxisSelections.every((selection, i) => {
        if (i === axisIndex) return true;
        if (selection === undefined || selection === null || selection === '') return true;
        return String(optionValueAt(variant, i)) === String(selection);
      });
    });

    if (matches.length === 0) return { state: 'invalid', variant: null };
    if (matches.length > 1) return { state: 'unresolved', variant: null };

    const variant = matches[0];
    return {
      state: variant.available ? 'resolved-available' : 'resolved-sold-out',
      variant,
    };
  }

  /**
   * Resolves the variant matching the complete current selection set
   * (every axis treated as already "chosen" at its current value) —
   * used for "what does the whole picker currently resolve to", as
   * opposed to resolveVariantCandidate's "what would this one row
   * resolve to if picked".
   *
   * @param {Array<string|null|undefined>} selections - current selection
   *   for every axis, blank/null/undefined entries are wildcards.
   * @param {Array<object>} variants - trusted variant array.
   */
  function resolveFromSelections(selections, variants) {
    if (!Array.isArray(variants) || !variants.length) {
      return { state: 'invalid', variant: null };
    }
    if (!selections.length || selections.every((s) => !s)) {
      return { state: 'unresolved', variant: null };
    }

    const matches = variants.filter((variant) =>
      selections.every((selection, i) => {
        if (selection === undefined || selection === null || selection === '') return true;
        return String(optionValueAt(variant, i)) === String(selection);
      }),
    );

    if (matches.length === 0) return { state: 'invalid', variant: null };
    if (matches.length > 1) return { state: 'unresolved', variant: null };

    const variant = matches[0];
    return {
      state: variant.available ? 'resolved-available' : 'resolved-sold-out',
      variant,
    };
  }

  // ── Purchase state ──────────────────────────────────────────────────────

  const PURCHASE_STATE = Object.freeze({
    UNRESOLVED: 'UNRESOLVED',
    AVAILABLE: 'AVAILABLE',
    PREORDER: 'PREORDER',
    WAITLIST: 'WAITLIST',
    UNAVAILABLE: 'UNAVAILABLE',
    INVALID: 'INVALID',
  });

  /**
   * Derives the provider-independent Purchase State from a resolution
   * result plus product-capability signals. Preorder always takes
   * precedence over waitlist. Provider identity never appears here.
   *
   * @param {'unresolved'|'resolved-available'|'resolved-sold-out'|'invalid'} resolutionState
   * @param {{ preorderActive?: boolean, waitlistCapable?: boolean }} capability
   */
  function computePurchaseState(resolutionState, capability) {
    const { preorderActive = false, waitlistCapable = false } = capability || {};

    switch (resolutionState) {
      case 'unresolved':
        return PURCHASE_STATE.UNRESOLVED;
      case 'invalid':
        return PURCHASE_STATE.INVALID;
      case 'resolved-available':
        return PURCHASE_STATE.AVAILABLE;
      case 'resolved-sold-out':
        if (preorderActive) return PURCHASE_STATE.PREORDER;
        if (waitlistCapable) return PURCHASE_STATE.WAITLIST;
        return PURCHASE_STATE.UNAVAILABLE;
      default:
        return PURCHASE_STATE.INVALID;
    }
  }

  // ── Journey branch state (Phase X) ──────────────────────────────────────
  // A journey step's *branch* (one axis, one value — e.g. "With Retail Box"
  // in a "Packaging" step) is a different question from either
  // resolveVariantCandidate (does this axis+value, combined with every
  // OTHER axis's current selection, resolve to exactly one variant?) or
  // resolveFromSelections (what does the whole current selection resolve
  // to?). A branch represents a whole *subtree* of not-yet-fully-selected
  // variants — later axes are deliberately still wildcards, since the
  // customer hasn't reached them yet. This is the same "prior selections
  // only" matching SyncVariantVisibility's own _status()/_priorMatch()
  // already use for branch-level sold-out detection — reused here via the
  // same shape (axisIndex, value, priorSelections) rather than duplicated,
  // so a branch's *purchasability* and its *waitlist explorability* are
  // always computed from the same matching variant set.

  const BRANCH_STATE = Object.freeze({
    PURCHASABLE: 'purchasable', // at least one matching descendant is available
    WAITLIST_EXPLORABLE: 'waitlist-explorable', // no available descendant, but at least one is WAITLIST-eligible
    UNAVAILABLE: 'unavailable', // no matching descendant at all, or descendants are preorder/incapable
  });

  /**
   * Classifies a journey branch as purchasable, waitlist-explorable, or
   * unavailable. Reuses the exact same capability signals
   * (preorderActive/waitlistCapable) computePurchaseState() already takes —
   * not a new eligibility system, just the same one applied to a branch's
   * full matching descendant set instead of a single resolved variant.
   *
   * Preorder is checked against the SAME global signal
   * getPurchaseState()/getPurchaseStateForCandidate() already use (the
   * theme's own preorder container visibility) — there is no per-variant
   * preorder detection in this architecture to draw on instead, so a branch
   * with a currently-preorder-active product is left UNAVAILABLE rather
   * than reclassified as explorable, per "it is not preorder — branches
   * containing preorder-only descendants remain governed by existing
   * Purchase State rules."
   *
   * @param {number} axisIndex - 0-based option position of the branch.
   * @param {string} value - the branch's option value.
   * @param {Array<string|null|undefined>} priorSelections - current
   *   selection for every axis; only entries BEFORE axisIndex are used as
   *   match constraints — axisIndex itself and every later axis are
   *   wildcards, since a branch spans every not-yet-chosen descendant.
   * @param {Array<object>} variants - trusted variant array.
   * @param {{ preorderActive?: boolean, waitlistCapable?: boolean }} capability
   * @returns {'purchasable'|'waitlist-explorable'|'unavailable'}
   */
  function classifyBranchState(axisIndex, value, priorSelections, variants, capability) {
    if (!Array.isArray(variants) || !variants.length) return BRANCH_STATE.UNAVAILABLE;

    const matches = variants.filter((variant) => {
      if (String(optionValueAt(variant, axisIndex)) !== String(value)) return false;
      return priorSelections.every((selection, i) => {
        if (i >= axisIndex) return true; // later axes (and this one) are wildcards
        if (selection === undefined || selection === null || selection === '') return true;
        return String(optionValueAt(variant, i)) === String(selection);
      });
    });

    if (!matches.length) return BRANCH_STATE.UNAVAILABLE; // structurally invalid — no such branch
    if (matches.some((variant) => variant.available)) return BRANCH_STATE.PURCHASABLE;

    const { preorderActive = false, waitlistCapable = false } = capability || {};
    if (preorderActive) return BRANCH_STATE.UNAVAILABLE;
    if (waitlistCapable) return BRANCH_STATE.WAITLIST_EXPLORABLE;
    return BRANCH_STATE.UNAVAILABLE;
  }

  // ── Waitlist row state (current-session) ────────────────────────────────
  // A row whose Purchase State is already WAITLIST can be in one of three
  // states describing the *customer's own action* on it this session — this
  // is deliberately a separate vocabulary from PURCHASE_STATE, which only
  // ever describes the variant's own condition and must never depend on
  // what a specific customer has done. Provider-independent, no STOQ terms.

  const WAITLIST_ROW_STATE = Object.freeze({
    ACTIONABLE: 'actionable', // "Notify me →" — eligible, not yet queued
    PENDING: 'pending', // "Watching" — queued, not yet submitted
    SUBMITTED: 'submitted', // "Notification set" — successfully submitted this session
  });

  /**
   * Resolves a row's current-session waitlist state. `eligible` must come
   * from computePurchaseState(...) === PURCHASE_STATE.WAITLIST — this
   * function doesn't re-derive eligibility, only the pending/submitted
   * layer on top of it. Returns null when the row has no waitlist
   * affordance at all (not eligible).
   *
   * @param {{ eligible: boolean, pending: boolean, submitted: boolean }} flags
   */
  function resolveWaitlistRowState(flags) {
    const { eligible, pending, submitted } = flags || {};
    if (submitted) return WAITLIST_ROW_STATE.SUBMITTED;
    if (pending) return WAITLIST_ROW_STATE.PENDING;
    if (eligible) return WAITLIST_ROW_STATE.ACTIONABLE;
    return null;
  }

  // ── Waitlist queue ──────────────────────────────────────────────────────

  /**
   * Normalises a variant ID to a string for consistent Set membership,
   * regardless of whether the caller passes a number or a string.
   */
  function normaliseVariantId(variantId) {
    if (variantId === undefined || variantId === null || variantId === '') return null;
    return String(variantId);
  }

  /**
   * Creates a small, provider-independent queue API around a backing
   * Set of exact Shopify variant IDs. The backing store is supplied by
   * the caller (so a DOM-anchored Set can be reused across instances)
   * rather than owned here — this factory only provides the operations.
   *
   * @param {Set<string>} store - backing Set, mutated in place.
   */
  function createWaitlistQueue(store) {
    if (!(store instanceof Set)) {
      throw new TypeError('createWaitlistQueue requires a Set backing store');
    }

    return {
      add(variantId) {
        const id = normaliseVariantId(variantId);
        if (id === null) return false;
        store.add(id);
        return true;
      },
      remove(variantId) {
        const id = normaliseVariantId(variantId);
        if (id === null) return false;
        return store.delete(id);
      },
      toggle(variantId) {
        const id = normaliseVariantId(variantId);
        if (id === null) return false;
        if (store.has(id)) {
          store.delete(id);
          return false;
        }
        store.add(id);
        return true;
      },
      has(variantId) {
        const id = normaliseVariantId(variantId);
        return id !== null && store.has(id);
      },
      clear() {
        store.clear();
      },
      replace(variantIds) {
        store.clear();
        (variantIds || []).forEach((variantId) => {
          const id = normaliseVariantId(variantId);
          if (id !== null) store.add(id);
        });
      },
      getAll() {
        return Array.from(store);
      },
      /**
       * Removes queue entries that are no longer valid: the variant must
       * still exist and still be sold out. Deliberately does NOT check
       * compatibility against the current selections — the waitlist queue
       * is independent of whatever the customer is currently browsing/
       * about to purchase (picking an available variant, or navigating to
       * a different combination, must not silently drop watched variants).
       * `selections` is accepted for signature stability but unused.
       */
      prune(selections, variants) {
        const removed = [];
        Array.from(store).forEach((id) => {
          const variant = (variants || []).find((v) => String(v.id) === id);
          const stillCompatible = variant && !variant.available;
          if (!stillCompatible) {
            store.delete(id);
            removed.push(id);
          }
        });
        return removed;
      },
    };
  }

  // ── Renderer helpers (Phase 2) ──────────────────────────────────────────
  // Pure, DOM-free string/formatting logic shared by any picker renderer
  // that needs to display queue-size-dependent copy (dropdown today,
  // potentially swatch/pill later). Kept here rather than in a picker file
  // so it's testable via node:test without jsdom, and reusable.

  /**
   * Naive-but-correct English pluraliser for an option name used as a noun
   * ("Grade" → "grades", "Colourway" → "colourways", "Box" → "boxes").
   * Lower-cases the result since it's used mid-sentence ("3 grades watching").
   */
  function pluralizeNoun(noun) {
    const lower = (noun || 'option').toLowerCase().trim() || 'option';
    if (/([sxz]|[cs]h)$/.test(lower)) return lower + 'es';
    if (/[^aeiou]y$/.test(lower)) return lower.slice(0, -1) + 'ies';
    return lower + 's';
  }

  /**
   * Replaces `[token]` placeholders in a template string with the supplied
   * values. Matches the convention already used by window.cartStrings /
   * window.variantStrings / window.quickOrderListStrings in theme.liquid
   * (Liquid's `| t: var: '[token]'` filter bakes the literal `[token]` text
   * into the string server-side; this performs the client-side substitution).
   */
  function formatTemplate(template, tokens) {
    let result = template || '';
    Object.keys(tokens || {}).forEach((key) => {
      result = result.split('[' + key + ']').join(tokens[key]);
    });
    return result;
  }

  /**
   * Classifies a provider submission result ({ succeeded, failed }) into
   * one of three outcomes a renderer needs to choose its UI state from.
   * Returns 'empty' for a no-op submission (shouldn't normally happen —
   * callers should not submit an empty queue — but defined rather than
   * left to throw).
   */
  function classifySubmissionOutcome(result) {
    const succeeded = result?.succeeded?.length || 0;
    const failed = result?.failed?.length || 0;
    if (succeeded === 0 && failed === 0) return 'empty';
    if (failed === 0) return 'success';
    if (succeeded === 0) return 'failure';
    return 'partial';
  }

  return {
    resolveVariantCandidate,
    resolveFromSelections,
    computePurchaseState,
    createWaitlistQueue,
    normaliseVariantId,
    PURCHASE_STATE,
    WAITLIST_ROW_STATE,
    resolveWaitlistRowState,
    pluralizeNoun,
    formatTemplate,
    classifySubmissionOutcome,
    BRANCH_STATE,
    classifyBranchState,
  };
});
